/**
 * M7 S13: Hale's deal and its voiding (spec §7, rev 2; break-it DS-3, EX-5; T10 logic half, T16 Hale half).
 *
 * - **The offer** is Hale's call (Mission 4's `hale` step). `haleAfterEvent` records its faction day once
 *   (`story.haleOfferDay`): only bounties older than it can be wiped, so the deal is never a planned-crime amnesty.
 *   At the call Hale learns what he can: the back route with the core already logged (`hale_aware`) gets the relay
 *   crew audited (a Mutual Bulletin item) and `hale_knows`; with `hale_knows` he traces Duarte's pulled digest
 *   (`duarte_exposed`).
 * - **The deal** (`takeHaleDeal`, the play path for the `deal` fork's `take`): (a) a corporation gets the charter
 *   standing gate waived and its fees refunded for the next `leverage.json hale.charters` charters
 *   (`setCharterWaiver`); a pilot without one gets the corporation founding fee waived (`corp.json founding.fee`);
 *   (b) the Authority bounties older than the offer are wiped up to `hale.bountyCap` (`wipeBounties`); (c) the
 *   Holdfast claims are registered (the fork's data command). Hale keeps the original 50-A (`withheld`), the player a
 *   copy (S4 `withholdItem`).
 * - **Voiding on Expose** (`voidHaleDeal`, from `exposeAuthority`): charters bought under it go under review, the
 *   wiped bounties come back × `hale.voidBountyMult`, a used fee waiver falls due as a registry debt, the Holdfast
 *   claims face a registry challenge (under review), and the waiver ends. `authority_exposed` needs the original
 *   50-A or Duarte's testimony, never a copy (`canExposeAuthority`).
 *
 * Pure: Career in, Career out; event-driven.
 */
import { z } from 'zod'
import type { Career } from '../career/types'
import corpJson from '../data/strategic/corp.json'
import { storyData } from './data'
import { storyItemOf } from './items'
import { missionOf, takeFork, type MissionCmd, type MissionContext } from './missions'
import { m3m4Config, wireCommand, type M3M4Config } from './okafor'
import type { LeverageConfig } from './schemas'
import { storyOf, withStory } from './state'
import type { HaleDeal, StoryCommand, StoryDebt, StoryEffect, StoryFlag, StoryParty, StoryResult } from './types'

// ---------------------------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------------------------

export interface HaleData {
  leverage: LeverageConfig
  hale: M3M4Config['hale']
  /** `strategic/corp.json founding.fee`: what a pilot without a corporation has waived. */
  foundingFee: number
}

const corpFoundingSchema = z.object({ founding: z.object({ fee: z.number().nonnegative() }) })

let cached: HaleData | undefined

/** The shipped data, read once. */
export function haleData(): HaleData {
  cached ??= {
    leverage: storyData().leverage,
    hale: m3m4Config().hale,
    foundingFee: corpFoundingSchema.parse(corpJson).founding.fee,
  }
  return cached
}

// ---------------------------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------------------------

/** A bounty on the player as the worker reports it: credits and the faction day it was issued. */
export interface BountyView {
  amount: number
  day: number
}

/** T16 (Hale half): the credits wiped, only bounties issued before `offerDay`, at most `cap`. */
export function haleBountyWipe(bounties: readonly BountyView[], offerDay: number, cap: number): number {
  let sum = 0
  for (const b of bounties) if (b.day < offerDay && b.amount > 0) sum += b.amount
  return Math.max(0, Math.min(cap, sum))
}

export interface HaleInput {
  /** `state.corp.faction`, null before founding. */
  corpFaction: StoryParty | null
  /** The player's bounties with the deal's faction (`m3m4.json hale.bountyFaction`). */
  bounties: readonly BountyView[]
}

/** What the deal gives, shown before commit (spec §1 rule 3) and applied by `takeHaleDeal`. */
export interface HaleTerms {
  offerDay: number
  /** Charters refunded with the standing gate waived (corporation only). */
  charters: number
  /** The founding fee waived (a pilot without a corporation only). */
  feeWaived: number
  bountyWipe: number
}

/** The deal's terms now, or null before Hale's call. */
export function haleTerms(career: Career, input: HaleInput, data: HaleData = haleData()): HaleTerms | null {
  const offerDay = storyOf(career).haleOfferDay
  if (offerDay === undefined) return null
  const corp = input.corpFaction !== null
  return {
    offerDay,
    charters: corp ? data.leverage.hale.charters : 0,
    feeWaived: corp ? 0 : data.foundingFee,
    bountyWipe: haleBountyWipe(input.bounties, offerDay, data.leverage.hale.bountyCap),
  }
}

function dealLive(career: Career): HaleDeal | null {
  const story = storyOf(career)
  if (story.flags.hale_deal !== true || story.flags.hale_deal_voided === true || story.hale === undefined) return null
  return story.hale
}

/** The founding fee a pilot founds without paying (0 without a live deal). The M6 founding flow reads it. */
export function haleFoundingFeeWaived(career: Career): number {
  return dealLive(career)?.feeWaived ?? 0
}

/** `authority_exposed` needs the original 50-A in the player's hands or Duarte's testimony, never a copy. */
export function canExposeAuthority(career: Career): boolean {
  if (storyOf(career).flags.duarte_testifies === true) return true
  return storyItemOf(career, 'settlement_50a')?.state === 'held'
}

// ---------------------------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------------------------

function withFlags(career: Career, flags: readonly StoryFlag[]): Career {
  const story = storyOf(career)
  if (flags.every((f) => story.flags[f] === true)) return career
  return withStory(career, (s) => {
    const next = { ...s.flags }
    for (const f of flags) next[f] = true
    return { ...s, flags: next }
  })
}

/**
 * After every story event (the story link calls it with the worker's faction day): the first time Mission 4 waits on
 * the deal fork, records the offer day and what Hale learns at the call (the audit, `hale_knows`, `duarte_exposed`).
 */
export function haleAfterEvent(career: Career, day: number, data: HaleData = haleData()): StoryResult<Career> {
  const m = missionOf(career, 'm4')
  const story = storyOf(career)
  if (m?.awaitingFork !== data.hale.dealFork || story.haleOfferDay !== undefined) return { career, commands: [], effects: [] }
  let next = withStory(career, (s) => ({ ...s, haleOfferDay: day }))
  const commands: StoryCommand[] = []
  const flags = storyOf(next).flags
  if (flags.archive_back === true && flags.hale_aware === true && flags.hale_knows !== true) {
    next = withFlags(next, ['hale_knows'])
    commands.push(wireCommand(data.hale.wire.audit))
  }
  if (storyOf(next).flags.hale_knows === true) next = withFlags(next, ['duarte_exposed'])
  return { career: next, commands, effects: [{ type: 'autosave', reason: 'hale:offer' }] }
}

/**
 * The player takes Hale's deal (the M4 `deal` fork's `take`, after its costs were shown): the fork's data outcome
 * (Hale keeps the original, the copy, `hale_deal`, the Holdfast claims) plus the deal record and its worker commands.
 */
export function takeHaleDeal(career: Career, input: HaleInput, ctx: MissionContext, data: HaleData = haleData()): MissionCmd {
  const terms = haleTerms(career, input, data)
  if (terms === null) return { ok: false, reason: 'noFork' }
  const r = takeFork(career, 'm4', data.hale.dealFork, data.hale.takeChoice, ctx)
  if (!r.ok) return r
  const deal: HaleDeal = {
    offerDay: terms.offerDay,
    chartersLeft: terms.charters,
    bountiesWiped: terms.bountyWipe,
    feeWaived: terms.feeWaived,
  }
  const next = withStory(r.result.career, (s) => ({ ...s, hale: deal }))
  const commands: StoryCommand[] = [...r.result.commands]
  if (terms.bountyWipe > 0) {
    commands.push({ type: 'wipeBounties', faction: data.hale.bountyFaction, olderThanDay: terms.offerDay, cap: data.leverage.hale.bountyCap })
  }
  if (terms.charters > 0) commands.push({ type: 'setCharterWaiver', charters: terms.charters, waiveMinStanding: true })
  return { ok: true, result: { career: next, commands, effects: r.result.effects } }
}

/** A charter bought under the live deal: one fewer left, its commission recorded (it goes under review if voided). */
export function haleCharterBought(career: Career, commissionId: string): StoryResult<Career> {
  const deal = dealLive(career)
  if (deal === null || deal.chartersLeft <= 0) return { career, commands: [], effects: [] }
  const charterIds = [...(deal.charterIds ?? []), commissionId]
  const next: HaleDeal = { ...deal, chartersLeft: deal.chartersLeft - 1, charterIds }
  return {
    career: withStory(career, (s) => ({ ...s, hale: next })),
    commands: [{ type: 'setCharterWaiver', charters: next.chartersLeft, waiveMinStanding: next.chartersLeft > 0 }],
    effects: [],
  }
}

export interface VoidView {
  /** `state.corp.faction` now: a fee waiver that was used (a corporation exists) falls due. */
  corpFaction: StoryParty | null
}

/** Voids a live deal (spec §7 "Deal, then Expose"); the same career when there is none. T10 logic half. */
export function voidHaleDeal(career: Career, view: VoidView, data: HaleData = haleData()): StoryResult<Career> {
  const deal = dealLive(career)
  if (deal === null) return { career, commands: [], effects: [] }
  const commands: StoryCommand[] = [{ type: 'setCharterWaiver', charters: 0, waiveMinStanding: false }]
  for (const cid of deal.charterIds ?? []) {
    commands.push({ type: 'setClaimState', scope: 'commission', id: cid, state: 'underReview', untilOnlineDay: null })
  }
  if (deal.bountiesWiped > 0) {
    commands.push({ type: 'restoreBounties', faction: data.hale.bountyFaction, amount: deal.bountiesWiped * data.leverage.hale.voidBountyMult })
  }
  commands.push({ type: 'setClaimState', scope: 'claim', id: data.hale.holdfastClaimId, state: 'underReview', untilOnlineDay: null })
  commands.push(wireCommand(data.hale.wire.voided))

  let next = withFlags(career, ['hale_deal_voided'])
  if (deal.feeWaived > 0 && view.corpFaction !== null) {
    const debt: StoryDebt = {
      kind: 'registryFee',
      creditor: 'authority',
      amount: deal.feeWaived,
      cyclesLeft: data.leverage.retaliation.dueCycles,
    }
    next = withStory(next, (s) => ({ ...s, debts: [...(s.debts ?? []), debt] }))
  }
  const effects: StoryEffect[] = [
    { type: 'comms', speaker: 'hale', nodeId: data.hale.comms.voided },
    { type: 'autosave', reason: 'hale:voided' },
  ]
  return { career: next, commands, effects }
}

/**
 * The Expose path's Authority half (S21 calls it at the hand-in): voids a live deal, seeds Hale's enmity bounty
 * when the player refused him, and sets `authority_exposed` only with the original 50-A or Duarte's testimony.
 */
export function exposeAuthority(career: Career, view: VoidView, data: HaleData = haleData()): StoryResult<Career> {
  const voided = voidHaleDeal(career, view, data)
  let next = voided.career
  const commands = [...voided.commands]
  const story = storyOf(next)
  if (story.flags.hale_refused === true && data.hale.enmityBounty > 0) {
    commands.push({ type: 'seedBounty', faction: data.hale.bountyFaction, amount: data.hale.enmityBounty, reason: 'hale-enmity' })
  }
  if (canExposeAuthority(next)) next = withFlags(next, ['authority_exposed'])
  return { career: next, commands, effects: voided.effects }
}
